import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import Swal from "sweetalert2";
import { UrlContext } from "../../contexts/UrlContext";

/**
 * ForcePasswordChangeModal
 * Blocking modal to force password change when ntbc (needs to be changed) is true.
 * Props:
 * - isOpen: boolean
 * - expertID: string | number
 * - onSuccess: () => void   // called when password updated successfully
 */
export default function ForcePasswordChangeModal({
  isOpen,
  expertID,
  onSuccess,
  dismissible = false,
  onClose,
}) {
  const { API_URL } = useContext(UrlContext);
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const currentRef = useRef(null);

  useEffect(() => {
    // Block background scroll when open
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
      setTimeout(() => currentRef.current?.focus(), 50);
    } else {
      document.body.classList.remove("overflow-hidden");
    }
    return () => document.body.classList.remove("overflow-hidden");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !dismissible) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, dismissible]);

  const handleClose = () => {
    setCurrentPwd("");
    setNewPwd("");
    setConfirmPwd("");
    setSubmitting(false);
    onClose?.();
  };

  // Password validation checks
  const checks = useMemo(() => {
    const minLen = newPwd.length >= 8;
    const upper = /[A-Z]/.test(newPwd);
    const lower = /[a-z]/.test(newPwd);
    const digit = /\d/.test(newPwd);
    const special = /[^A-Za-z0-9]/.test(newPwd);
    const notSameAsOld = newPwd && currentPwd && newPwd !== currentPwd;
    const match = newPwd && confirmPwd && newPwd === confirmPwd;
    return { minLen, upper, lower, digit, special, notSameAsOld, match };
  }, [newPwd, confirmPwd, currentPwd]);

  const allValid =
    checks.minLen &&
    checks.upper &&
    checks.lower &&
    checks.digit &&
    checks.special &&
    checks.notSameAsOld &&
    checks.match &&
    currentPwd.length > 0;

  const updatePassword = async () => {
    if (!allValid || submitting) return;
    setSubmitting(true);
    try {
      const token =
        localStorage.getItem("AccessToken") ||
        localStorage.getItem("linkedinToken");
      const res = await fetch(`${API_URL}reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
        body: JSON.stringify({
          oldpass: currentPwd,
          newpass: newPwd,
        }),
      });
      const data = await res.json();
      if (data.status === "success") {
        Swal.fire({
          title: "Password updated",
          text: "Your password has been changed successfully.",
          icon: "success",
          timer: 1800,
          showConfirmButton: false,
        });
        localStorage.removeItem("ntbc");
        onSuccess?.();
      } else {
        Swal.fire({
          title: "Update failed",
          text: data.message || "We couldn't change your password.",
          icon: "error",
          timer: 2500,
        });
      }
    } catch (e) {
      Swal.fire({
        title: "Network error",
        text: "Please try again in a moment.",
        icon: "error",
        timer: 2500,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const Check = ({ ok }) => (
    <i
      className={`fa-solid ${
        ok ? "fa-check text-green-600" : "fa-xmark text-gray-400"
      }`}
      aria-hidden
    />
  );

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      aria-modal="true"
      role="dialog"
    >
      <div className="bg-white w-[95%] max-w-[520px] rounded-lg shadow-xl p-6 relative">
        {dismissible && (
          <button
            type="button"
            className="absolute right-3 top-3 text-gray-500 hover:text-gray-800"
            aria-label="Close"
            onClick={handleClose}
          >
            <i className="fa-solid fa-xmark text-xl" />
          </button>
        )}
        <h2 className="text-xl font-semibold text-gray-900 mb-1">
          Update your password
        </h2>
        {!dismissible && (
          <p className="text-sm text-gray-600 mb-4">
            For your security, please change the temporary password you used to
            log in.
          </p>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current password
            </label>
            <div className="relative">
              <input
                ref={currentRef}
                type={showCurrent ? "text" : "password"}
                className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#365561]"
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                onClick={() => setShowCurrent((s) => !s)}
                aria-label={showCurrent ? "Hide password" : "Show password"}
              >
                <i
                  className={`fa-solid ${
                    showCurrent ? "fa-eye-slash" : "fa-eye"
                  }`}
                />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New password
            </label>
            <div className="relative">
              <input
                type={showNew ? "text" : "password"}
                className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#365561]"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                onClick={() => setShowNew((s) => !s)}
                aria-label={showNew ? "Hide password" : "Show password"}
              >
                <i
                  className={`fa-solid ${showNew ? "fa-eye-slash" : "fa-eye"}`}
                />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm new password
            </label>
            <div className="relative">
              <input
                type={showConfirm ? "text" : "password"}
                className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#365561]"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                onClick={() => setShowConfirm((s) => !s)}
                aria-label={showConfirm ? "Hide password" : "Show password"}
              >
                <i
                  className={`fa-solid ${
                    showConfirm ? "fa-eye-slash" : "fa-eye"
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="bg-gray-50 rounded-md p-3 text-xs text-gray-700">
            <div className="flex items-center gap-2 mb-1">
              <Check ok={checks.minLen} />
              <span>At least 8 characters</span>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <Check ok={checks.upper} />
              <span>At least one uppercase letter</span>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <Check ok={checks.lower} />
              <span>At least one lowercase letter</span>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <Check ok={checks.digit} />
              <span>At least one number</span>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <Check ok={checks.special} />
              <span>At least one special character</span>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <Check ok={checks.notSameAsOld} />
              <span>Different from current password</span>
            </div>
            <div className="flex items-center gap-2">
              <Check ok={checks.match} />
              <span>New password and confirmation match</span>
            </div>
          </div>

          <button
            type="button"
            onClick={updatePassword}
            disabled={!allValid || submitting}
            className={`w-full mt-2 rounded-md px-4 py-2 text-white ${
              allValid && !submitting
                ? "bg-[#365561] hover:bg-[#2d4753]"
                : "bg-gray-300 cursor-not-allowed"
            }`}
          >
            {submitting ? "Saving..." : "Change password"}
          </button>

          {!dismissible && (
            <p className="text-[11px] text-gray-500 mt-2">
              This action is required. You won't be able to continue using the
              app until your password is updated.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
